import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr } from "../../lib/api";
import { Card, EmptyState, Field, FileList, Spinner, inputCls } from "../ui";
import { FilePicker } from "../../components/QuoteSection";
import { useAuth } from "../../auth/AuthContext";

const TABS = ["Opportunities", "My Offers", "Profile"];

function OfferForm({ opp, onDone }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    price_per_unit: "", currency: "NGN", quantity: opp.request?.quantity || "",
    delivery_timeline: "", payment_terms: "", validity: "", conditions: "", spec_confirmed: false,
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, String(v)));
      files.forEach((f) => fd.append("files", f));
      await api.post(`/supplier/opportunities/${opp.id}/offer`, fd);
      toast.success("Offer submitted", { description: "Ajemi will review your offer." });
      onDone();
    } catch (err) {
      toast.error("Submission failed", { description: fmtErr(err) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5 border-t border-white/8 pt-5" data-testid={`offer-form-${opp.id}`}>
      <Field label="Price per Unit (₦)" required>
        <input required type="number" step="0.01" min="0" value={form.price_per_unit} onChange={set("price_per_unit")} className={inputCls} placeholder="e.g. 1700" data-testid="offer-price-input" />
      </Field>
      <Field label="Available Quantity">
        <input type="number" min="0" value={form.quantity} onChange={set("quantity")} className={inputCls} data-testid="offer-quantity-input" />
      </Field>
      <Field label="Delivery Timeline">
        <input value={form.delivery_timeline} onChange={set("delivery_timeline")} className={inputCls} placeholder="e.g. 5 days from confirmation" data-testid="offer-timeline-input" />
      </Field>
      <Field label="Quote Validity">
        <input value={form.validity} onChange={set("validity")} className={inputCls} placeholder="e.g. 7 days" data-testid="offer-validity-input" />
      </Field>
      <Field label="Payment Terms">
        <input value={form.payment_terms} onChange={set("payment_terms")} className={inputCls} placeholder="e.g. 50% advance" data-testid="offer-terms-input" />
      </Field>
      <Field label="Additional Conditions">
        <input value={form.conditions} onChange={set("conditions")} className={inputCls} data-testid="offer-conditions-input" />
      </Field>
      <label className="sm:col-span-2 flex items-center gap-3 text-sm text-white/70 cursor-pointer">
        <input type="checkbox" checked={form.spec_confirmed} onChange={set("spec_confirmed")} className="accent-[#D9A441] w-4 h-4" data-testid="offer-spec-checkbox" />
        I confirm the product specification meets the requirement
      </label>
      <div className="sm:col-span-2">
        <FilePicker files={files} setFiles={setFiles} testId={`offer-file-upload-${opp.id}`} />
      </div>
      <div className="sm:col-span-2">
        <button type="submit" disabled={loading} className="inline-flex items-center gap-2 bg-gold text-ink text-sm font-outfit font-medium rounded-full px-7 py-3 hover:bg-goldbright transition-colors disabled:opacity-60" data-testid="offer-submit-btn">
          {loading ? (<><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</>) : opp.my_offer ? "Update Offer" : "Submit Offer"}
        </button>
      </div>
    </form>
  );
}

function Opportunities() {
  const [items, setItems] = useState(null);
  const [supplierStatus, setSupplierStatus] = useState("active");
  const [openOffer, setOpenOffer] = useState(null);

  const load = () => api.get("/supplier/opportunities").then((r) => { setItems(r.data.items); setSupplierStatus(r.data.supplier_status); }).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  if (!items) return <div className="py-16"><Spinner /></div>;
  if (supplierStatus === "pending") {
    return <EmptyState title="Account under review" text="Ajemi is reviewing your supplier profile. You'll be notified once approved — complete your profile in the meantime." />;
  }
  if (items.length === 0) {
    return <EmptyState title="No opportunities yet" text="Ajemi shares qualified procurement opportunities with selected supply partners. You'll be notified when one matches your capability." />;
  }
  return (
    <div className="space-y-5" data-testid="opportunities-list">
      {items.map((opp) => (
        <Card key={opp.id} testId={`opportunity-${opp.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-outfit text-lg text-white">{opp.request?.product || "Procurement Opportunity"}</p>
              <p className="text-xs text-white/40 mt-1">{opp.request?.ref} · {opp.request?.type === "one_time" ? "One-Time" : opp.request?.type}</p>
            </div>
            <span className={`text-[10px] tracking-[0.15em] uppercase border rounded-full px-3 py-1 ${
              opp.status === "awarded" ? "border-emerald-400/50 text-emerald-300"
              : opp.status === "offer_submitted" ? "border-gold/50 text-gold"
              : opp.status === "not_selected" ? "border-white/15 text-white/40"
              : "border-volt/40 text-volt"
            }`}>
              {opp.status === "offer_submitted" ? "Offer Submitted" : opp.status.replace("_", " ")}
            </span>
          </div>
          {opp.request && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5 text-sm">
              <div><p className="text-white/35 text-xs uppercase tracking-wider">Quantity</p><p className="text-white/80 mt-1">{Number(opp.request.quantity || 0).toLocaleString()} {opp.request.unit}</p></div>
              <div><p className="text-white/35 text-xs uppercase tracking-wider">Location</p><p className="text-white/80 mt-1">{opp.request.delivery_location}</p></div>
              <div><p className="text-white/35 text-xs uppercase tracking-wider">Required</p><p className="text-white/80 mt-1">{opp.request.required_date || opp.request.start_date || "—"}</p></div>
              <div><p className="text-white/35 text-xs uppercase tracking-wider">Frequency</p><p className="text-white/80 mt-1">{opp.request.frequency || "One-time"}</p></div>
            </div>
          )}
          {opp.request?.specs && <p className="text-xs text-white/45 mt-4">Specs: {opp.request.specs}</p>}
          {opp.my_offer && (
            <p className="text-sm text-gold mt-4" data-testid={`my-offer-${opp.id}`}>
              Your offer: ₦{Number(opp.my_offer.price_per_unit).toLocaleString()}/{opp.request?.unit?.replace(/s$/, "")} · {opp.my_offer.status}
            </p>
          )}
          {!["awarded", "not_selected"].includes(opp.status) && (
            openOffer === opp.id ? (
              <OfferForm opp={opp} onDone={() => { setOpenOffer(null); load(); }} />
            ) : (
              <button onClick={() => setOpenOffer(opp.id)} className="mt-5 inline-flex items-center gap-2 border border-gold/50 text-gold text-sm font-outfit rounded-full px-6 py-2.5 hover:bg-gold hover:text-ink transition-colors" data-testid={`submit-offer-btn-${opp.id}`}>
                {opp.my_offer ? "Update Offer" : "Submit Offer"}
              </button>
            )
          )}
        </Card>
      ))}
    </div>
  );
}

function MyOffers() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/supplier/offers").then((r) => setItems(r.data.items)).catch(() => setItems([])); }, []);
  if (!items) return <div className="py-16"><Spinner /></div>;
  if (items.length === 0) return <EmptyState title="No offers yet" text="Offers you submit on procurement opportunities will appear here." />;
  return (
    <div className="overflow-x-auto" data-testid="offers-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-white/35 border-b border-white/8">
            <th className="pb-3 pr-4">Request</th><th className="pb-3 pr-4">Product</th><th className="pb-3 pr-4">Price</th><th className="pb-3 pr-4">Status</th><th className="pb-3">Submitted</th>
          </tr>
        </thead>
        <tbody>
          {items.map((o) => (
            <tr key={o.id} className="border-b border-white/5">
              <td className="py-4 pr-4 text-gold font-outfit">{o.request?.ref || "—"}</td>
              <td className="py-4 pr-4 text-white/75">{o.request?.product}</td>
              <td className="py-4 pr-4 text-white/75">₦{Number(o.price_per_unit).toLocaleString()}</td>
              <td className="py-4 pr-4">
                <span className={`text-[10px] tracking-[0.15em] uppercase border rounded-full px-3 py-1 ${o.status === "selected" ? "border-emerald-400/50 text-emerald-300" : o.status === "rejected" ? "border-white/15 text-white/40" : "border-gold/50 text-gold"}`}>
                  {o.status}
                </span>
              </td>
              <td className="py-4 text-white/40">{new Date(o.created_at).toLocaleDateString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Profile() {
  const { setUser } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/supplier/profile").then((r) => {
      const u = r.data.user;
      setForm({
        company_name: u.company_name, contact_name: u.contact_name, phone: u.phone,
        profile: {
          products_supplied: "", supply_locations: "", available_volumes: "", delivery_capabilities: "",
          business_info: "", certifications: "", banking_info: "", website: "", ...u.profile,
        },
      });
    });
  }, []);

  if (!form) return <div className="py-16"><Spinner /></div>;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setP = (k) => (e) => setForm((f) => ({ ...f, profile: { ...f.profile, [k]: e.target.value } }));

  const save = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const res = await api.put("/supplier/profile", form);
      setUser(res.data.user);
      toast.success("Profile saved");
    } catch (err) {
      toast.error("Save failed", { description: fmtErr(err) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-3xl" data-testid="supplier-profile-form">
      <Field label="Company Name" required><input required value={form.company_name} onChange={set("company_name")} className={inputCls} data-testid="sp-company-input" /></Field>
      <Field label="Contact Name" required><input required value={form.contact_name} onChange={set("contact_name")} className={inputCls} data-testid="sp-contact-input" /></Field>
      <Field label="Phone"><input value={form.phone} onChange={set("phone")} className={inputCls} data-testid="sp-phone-input" /></Field>
      <Field label="Website"><input value={form.profile.website} onChange={setP("website")} className={inputCls} placeholder="https://" data-testid="sp-website-input" /></Field>
      <Field label="Products Supplied"><input value={form.profile.products_supplied} onChange={setP("products_supplied")} className={inputCls} placeholder="e.g. Jet A-1, AGO, PMS" data-testid="sp-products-input" /></Field>
      <Field label="Supply Locations"><input value={form.profile.supply_locations} onChange={setP("supply_locations")} className={inputCls} placeholder="e.g. Lagos, Abuja, PH" data-testid="sp-locations-input" /></Field>
      <Field label="Available Volumes"><input value={form.profile.available_volumes} onChange={setP("available_volumes")} className={inputCls} placeholder="e.g. 2M litres / month" data-testid="sp-volumes-input" /></Field>
      <Field label="Delivery Capabilities"><input value={form.profile.delivery_capabilities} onChange={setP("delivery_capabilities")} className={inputCls} placeholder="Trucking, marine, rail…" data-testid="sp-delivery-input" /></Field>
      <div className="sm:col-span-2"><Field label="Business Information"><textarea rows={3} value={form.profile.business_info} onChange={setP("business_info")} className={`${inputCls} resize-none`} data-testid="sp-business-input" /></Field></div>
      <div className="sm:col-span-2"><Field label="Certifications & Compliance"><textarea rows={2} value={form.profile.certifications} onChange={setP("certifications")} className={`${inputCls} resize-none`} data-testid="sp-certifications-input" /></Field></div>
      <div className="sm:col-span-2"><Field label="Banking / Payment Information"><textarea rows={2} value={form.profile.banking_info} onChange={setP("banking_info")} className={`${inputCls} resize-none`} placeholder="Only what is legitimately required for settlement" data-testid="sp-banking-input" /></Field></div>
      <div className="sm:col-span-2">
        <button type="submit" disabled={saving} className="bg-gold text-ink font-outfit font-medium rounded-full px-8 py-3 hover:bg-goldbright transition-colors disabled:opacity-60" data-testid="sp-save-btn">
          {saving ? "Saving…" : "Save Profile"}
        </button>
      </div>
    </form>
  );
}

export default function SupplierDashboard() {
  const [tab, setTab] = useState("Opportunities");
  return (
    <div data-testid="supplier-dashboard">
      <h1 className="font-outfit text-3xl font-extralight tracking-tight text-white mb-2">
        Supplier <span className="text-gold">Portal</span>
      </h1>
      <p className="text-sm text-white/50 mb-8">Access qualified procurement opportunities from Ajemi.</p>
      <div className="flex gap-2 mb-8 border-b border-white/8" data-testid="supplier-tabs">
        {TABS.map((t) => (
          <button
            key={t}
            data-testid={`supplier-tab-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            onClick={() => setTab(t)}
            className={`px-5 py-3 text-sm font-outfit border-b-2 transition-colors -mb-px ${tab === t ? "border-gold text-gold" : "border-transparent text-white/50 hover:text-white"}`}
          >
            {t}
          </button>
        ))}
      </div>
      {tab === "Opportunities" && <Opportunities />}
      {tab === "My Offers" && <MyOffers />}
      {tab === "Profile" && <Profile />}
    </div>
  );
}
